import React, { useState, useEffect, useCallback } from 'react';
import chemicalService from '../../../services/chemicalService';
import FormulaFormModal from '../components/FormulaFormModal';

const PAGE_SIZE = 10;

const FormulasTab = () => {
  const [formulas, setFormulas] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingFormula, setEditingFormula] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [activeFilter, debouncedSearch]);

  const fetchFormulas = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (activeFilter !== 'all') params.is_active = activeFilter === 'active';
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      const [listRes, summaryRes] = await Promise.all([
        chemicalService.getFormulas(params),
        chemicalService.getFormulasSummary().catch((e) => {
          console.warn('Formula summary endpoint not available:', e.message);
          return { data: { total: 0, active: 0 } };
        }),
      ]);

      setFormulas(listRes.data || []);
      setPagination(listRes.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
      setSummary(summaryRes.data || { total: 0, active: 0 });
    } catch (err) {
      console.error('Error fetching recipes:', err);
      alert('❌ ' + (err.response?.data?.message || 'Failed to load recipes'));
    } finally {
      setLoading(false);
    }
  }, [page, activeFilter, debouncedSearch]);

  useEffect(() => {
    fetchFormulas();
  }, [fetchFormulas]);

  const handleAddClick = () => {
    setEditingFormula(null);
    setShowFormModal(true);
  };

  // When editing, fetch the full formula (with ingredients) before opening modal
  const handleEditClick = async (f) => {
    try {
      setLoadingDetail(true);
      const res = await chemicalService.getFormulaById(f.id);
      setEditingFormula(res.data);
      setShowFormModal(true);
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to load recipe details'));
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`Delete recipe "${f.code}" (${f.product_name})?\n\nThis cannot be undone.`)) return;
    try {
      await chemicalService.deleteFormula(f.id);
      alert('✅ Recipe deleted successfully!');
      if (formulas.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchFormulas();
      }
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to delete recipe'));
    }
  };

  const handleSaved = () => {
    setShowFormModal(false);
    setEditingFormula(null);
    fetchFormulas();
  };

  return (
    <div style={{ padding: '24px 30px 40px' }}>

      {/* ─── Summary Cards ───────────────────────── */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #2c3e50' }}>
          <div style={styles.summaryLabel}>Total Recipes</div>
          <div style={{ ...styles.summaryValue, color: '#2c3e50' }}>{summary.total}</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #27ae60' }}>
          <div style={styles.summaryLabel}>Active</div>
          <div style={{ ...styles.summaryValue, color: '#27ae60' }}>{summary.active}</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #95a5a6' }}>
          <div style={styles.summaryLabel}>Retired</div>
          <div style={{ ...styles.summaryValue, color: '#95a5a6' }}>
            {Math.max(0, summary.total - summary.active)}
          </div>
        </div>
      </div>

      {/* ─── Filters + Search + Add ──────────────── */}
      <div style={styles.toolbar}>
        <div style={styles.filterTabs}>
          {[
            { id: 'all', label: 'All' },
            { id: 'active', label: 'Active' },
            { id: 'inactive', label: 'Retired' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              style={{
                ...styles.filterButton,
                background: activeFilter === f.id ? '#e74c3c' : 'white',
                color: activeFilter === f.id ? 'white' : '#2c3e50',
                borderColor: activeFilter === f.id ? '#e74c3c' : '#e0e0e0',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div style={styles.toolbarRight}>
          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}>⌕</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by recipe code or product name..."
              style={styles.searchInput}
            />
          </div>
          <button onClick={handleAddClick} style={styles.addButton}>
            + New recipe
          </button>
        </div>
      </div>

      {/* ─── Formula Cards Grid ──────────────────── */}
      {loading ? (
        <div style={styles.emptyState}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>⏳</div>
          <div>Loading recipes...</div>
        </div>
      ) : formulas.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>⚗️</div>
          <div>
            {debouncedSearch || activeFilter !== 'all'
              ? 'No recipes match your filters'
              : 'No recipes yet — click "+ New recipe" to create one'}
          </div>
        </div>
      ) : (
        <div style={styles.formulasGrid}>
          {formulas.map((f) => {
            const yieldRatio = f.total_input_qty > 0
              ? (parseFloat(f.yield_qty) / parseFloat(f.total_input_qty)) * 100
              : null;

            return (
              <div key={f.id} style={{ ...styles.formulaCard, opacity: f.is_active ? 1 : 0.55 }}>
                <div style={styles.cardHeader}>
                  <div>
                    <div style={styles.cardCode}>{f.code}</div>
                    <div style={styles.cardProduct}>{f.product_name}</div>
                  </div>
                  <span style={f.is_active ? styles.activeBadge : styles.inactiveBadge}>
                    {f.is_active ? 'Active' : 'Retired'}
                  </span>
                </div>

                <div style={styles.cardStats}>
                  <div style={styles.statItem}>
                    <div style={styles.statLabel}>Yield</div>
                    <div style={styles.statValue}>
                      {formatQty(f.yield_qty)} <span style={styles.statUnit}>{f.product_unit}</span>
                    </div>
                  </div>
                  <div style={styles.statItem}>
                    <div style={styles.statLabel}>Total Input</div>
                    <div style={styles.statValue}>
                      {formatQty(f.total_input_qty)}
                    </div>
                  </div>
                  <div style={styles.statItem}>
                    <div style={styles.statLabel}>Ingredients</div>
                    <div style={styles.statValue}>
                      {f.ingredient_count || 0}
                    </div>
                  </div>
                  <div style={styles.statItem}>
                    <div style={styles.statLabel}>Yield Ratio</div>
                    <div style={styles.statValue}>
                      {yieldRatio != null ? `${yieldRatio.toFixed(1)}%` : '—'}
                    </div>
                  </div>
                </div>

                <div style={styles.cardMeta}>
                  <span>Version {f.version || 1}</span>
                  {f.updated_at && (
                    <span>Updated {formatDate(f.updated_at)}</span>
                  )}
                </div>

                <div style={styles.cardActions}>
                  <button
                    onClick={() => handleEditClick(f)}
                    style={styles.viewButton}
                    disabled={loadingDetail}
                  >
                    {loadingDetail ? '⏳ Loading...' : '✏️ View / Edit'}
                  </button>
                  <button
                    onClick={() => handleDelete(f)}
                    style={styles.deleteButton}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Pagination ──────────────────────────── */}
      {!loading && pagination.total > 0 && (
        <div style={styles.paginationWrap}>
          <div style={styles.paginationInfo}>
            Showing {(pagination.page - 1) * pagination.limit + 1}
            –{Math.min(pagination.page * pagination.limit, pagination.total)}
            {' '}of {pagination.total} recipes
          </div>
          <div style={styles.paginationButtons}>
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              «
            </button>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              ‹ Prev
            </button>
            <span style={styles.pageIndicator}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              Next ›
            </button>
            <button
              onClick={() => setPage(pagination.totalPages)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* ─── Add/Edit Modal ──────────────────────── */}
      {showFormModal && (
        <FormulaFormModal
          formula={editingFormula}
          onClose={() => { setShowFormModal(false); setEditingFormula(null); }}
          onSaved={handleSaved}
        />
      )}

    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────

const formatQty = (n) => {
  const num = parseFloat(n) || 0;
  return num % 1 === 0 ? num.toLocaleString('en-PK') : num.toFixed(2);
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Styles ───────────────────────────────────────────────

const styles = {
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  summaryCard: {
    background: 'white',
    padding: '18px 22px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  summaryLabel: {
    fontSize: '13px', color: '#7f8c8d',
    marginBottom: '6px', fontWeight: '600',
  },
  summaryValue: {
    fontSize: '28px', fontWeight: '700',
  },

  toolbar: {
    background: 'white',
    padding: '16px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', flexWrap: 'wrap', gap: '16px',
    justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '20px',
  },
  filterTabs: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  filterButton: {
    padding: '8px 16px',
    border: '2px solid', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', transition: 'all 0.2s',
  },
  toolbarRight: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  searchWrap: { position: 'relative' },
  searchIcon: {
    position: 'absolute', left: '12px', top: '50%',
    transform: 'translateY(-50%)',
    color: '#95a5a6', fontSize: '16px',
  },
  searchInput: {
    padding: '10px 12px 10px 36px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', width: '280px',
    fontFamily: 'inherit',
  },
  addButton: {
    padding: '10px 20px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },

  formulasGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '16px',
  },
  formulaCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  cardCode: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#e74c3c',
    marginBottom: '4px',
    letterSpacing: '0.5px',
  },
  cardProduct: {
    fontSize: '17px',
    fontWeight: '700',
    color: '#2c3e50',
  },
  cardStats: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    padding: '14px',
    background: '#f8f9fa',
    borderRadius: '10px',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  statLabel: {
    fontSize: '11px',
    color: '#7f8c8d',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  statValue: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#2c3e50',
  },
  statUnit: {
    fontSize: '12px',
    color: '#7f8c8d',
    fontWeight: '500',
  },
  cardMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: '#95a5a6',
  },
  cardActions: {
    display: 'flex',
    gap: '8px',
  },
  viewButton: {
    flex: 1,
    padding: '10px',
    background: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '700',
  },
  deleteButton: {
    padding: '10px 14px',
    background: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
  },

  emptyState: {
    background: 'white',
    borderRadius: '12px',
    padding: '60px 20px',
    textAlign: 'center',
    color: '#95a5a6',
    fontSize: '15px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },

  activeBadge: {
    background: '#d4edda', color: '#155724',
    padding: '4px 12px', borderRadius: '12px',
    fontSize: '12px', fontWeight: '600',
  },
  inactiveBadge: {
    background: '#e2e3e5', color: '#383d41',
    padding: '4px 12px', borderRadius: '12px',
    fontSize: '12px', fontWeight: '600',
  },

  paginationWrap: {
    marginTop: '20px',
    background: 'white',
    padding: '14px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', flexWrap: 'wrap', gap: '12px',
  },
  paginationInfo: { fontSize: '13px', color: '#7f8c8d' },
  paginationButtons: { display: 'flex', gap: '6px', alignItems: 'center' },
  pageButton: {
    padding: '8px 14px',
    background: 'white', color: '#2c3e50',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '600',
  },
  pageButtonDisabled: {
    opacity: 0.4, cursor: 'not-allowed',
  },
  pageIndicator: {
    padding: '0 12px', fontSize: '13px',
    color: '#2c3e50', fontWeight: '600',
  },
};

export default FormulasTab;